/**
 * Consent Store — RGPD/cookie consent state
 */

import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'

export interface ConsentPreferences {
  necessary: true
  functional: boolean
  analytics: boolean
  marketing: boolean
  ai_personalization: boolean
}

export interface ConsentRecord {
  id: string
  preferences: ConsentPreferences
  timestamp: string
  source: 'banner' | 'settings' | 'api'
}

interface ConsentState {
  hasResponded: boolean
  showBanner: boolean
  record: ConsentRecord | null
  history: ConsentRecord[]

  // Actions
  acceptAll: () => void
  refuseAll: () => void
  savePreferences: (prefs: ConsentPreferences) => void
  dismissBanner: () => void
  reset: () => void
}

const defaultPrefs: ConsentPreferences = {
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
  ai_personalization: false,
}

function makeRecord(prefs: ConsentPreferences, source: 'banner' | 'settings'): ConsentRecord {
  return {
    id: crypto.randomUUID(),
    preferences: prefs,
    timestamp: new Date().toISOString(),
    source,
  }
}

export const useConsentStore = create<ConsentState>()(
  devtools(
    persist(
      (set) => ({
        hasResponded: false,
        showBanner: true,
        record: null,
        history: [],

        acceptAll: () => {
          const prefs: ConsentPreferences = {
            necessary: true,
            functional: true,
            analytics: true,
            marketing: true,
            ai_personalization: true,
          }
          const rec = makeRecord(prefs, 'banner')
          set(
            (state) => ({
              hasResponded: true,
              showBanner: false,
              record: rec,
              history: [...state.history, rec],
            }),
            false, 'acceptAll'
          )
        },

        refuseAll: () => {
          const rec = makeRecord(defaultPrefs, 'banner')
          set(
            (state) => ({
              hasResponded: true,
              showBanner: false,
              record: rec,
              history: [...state.history, rec],
            }),
            false, 'refuseAll'
          )
        },

        savePreferences: (prefs) => {
          const rec = makeRecord(prefs, 'settings')
          set(
            (state) => ({
              hasResponded: true,
              showBanner: false,
              record: rec,
              history: [...state.history, rec],
            }),
            false, 'savePreferences'
          )
        },

        dismissBanner: () =>
          set({ showBanner: false }, false, 'dismissBanner'),

        reset: () =>
          set({
            hasResponded: false,
            showBanner: true,
            record: null,
            history: [],
          }, false, 'reset'),
      }),
      {
        name: 'sgrc-consent',
      }
    ),
    { name: 'ConsentStore' }
  )
)
